import type { SelectOption } from '@/config/surveyOptions';

interface SelectionCardsProps {
  name: string;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  columns?: 2 | 3;
  disabled?: boolean;
}

/**
 * Large single-selection cards with real radio-button semantics: exactly one
 * option can be chosen, arrow keys move between them, and screen readers
 * announce the group correctly.
 */
export function SelectionCards({
  name,
  options,
  value,
  onChange,
  columns = 2,
  disabled = false,
}: SelectionCardsProps) {
  return (
    <div
      className={`grid gap-2 ${columns === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}
      role="radiogroup"
      aria-label={name}
    >
      {options.map((option) => {
        const selected = option.value === value;
        const id = `${name}-${option.value}`;
        return (
          <label
            key={option.value}
            htmlFor={id}
            className={[
              'tap-target flex min-h-[56px] cursor-pointer items-center justify-center rounded-xl border-2 px-2 py-3 text-center text-base font-semibold transition-colors',
              selected
                ? 'border-brand-700 bg-brand-700 text-white'
                : 'border-slate-300 bg-white text-slate-800 hover:bg-slate-50',
              disabled ? 'cursor-not-allowed opacity-50' : '',
            ].join(' ')}
          >
            <input
              id={id}
              type="radio"
              name={name}
              value={option.value}
              checked={selected}
              disabled={disabled}
              onChange={() => onChange(option.value)}
              className="sr-only"
            />
            {option.label}
          </label>
        );
      })}
    </div>
  );
}
